import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { CalendarDays, Clock, User, Trash2, Search, UserPlus, LogIn } from "lucide-react";
import { api } from "@/lib/api";
import SEOHead from "@/components/SEOHead";

export default function MiCita() {
  const [form, setForm] = useState({ email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/bookings/lookup", form);
      setResults(r.data);
    } catch { toast.error("No se pudo consultar"); }
    finally { setLoading(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm("¿Cancelar esta cita? No podrás recuperarla.")) return;
    try {
      await api.delete(`/bookings/${id}`);
      setResults((r) => (r || []).filter((b) => b.id !== id));
      toast.success("Cita cancelada");
    } catch { toast.error("No se pudo cancelar"); }
  };

  const upcoming = (results || []).filter((b) => new Date(`${b.slot_date}T${b.slot_time}`) >= new Date());
  const past = (results || []).filter((b) => new Date(`${b.slot_date}T${b.slot_time}`) < new Date());

  return (
    <>
      <SEOHead
        title="Mi cita · Katia Falón Odontología"
        description="Consulta, verifica o cancela tus citas con Katia Falón Odontología ingresando tu correo y teléfono."
        path="/mi-cita"
      />
      <section data-testid="mi-cita-page" className="mx-auto max-w-3xl px-6 py-16 lg:py-24">
        <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--clinic-muted))]">Portal del paciente</div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl">Consulta tu cita</h1>
        <p className="mt-4 text-[hsl(var(--clinic-muted))]">
          Verificamos con tu correo y los <strong>4 últimos dígitos del teléfono</strong> con los que reservaste. Sin registrarte, sin contraseñas.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/paciente/registro" data-testid="mi-cita-register-cta" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--clinic-secondary))] px-5 py-2.5 text-sm font-semibold text-white">
            <UserPlus size={14} /> Crear cuenta completa
          </Link>
          <Link to="/paciente/login" data-testid="mi-cita-login-cta" className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--clinic-border))] px-5 py-2.5 text-sm font-semibold">
            <LogIn size={14} /> Ya tengo cuenta
          </Link>
        </div>
        <p className="mt-3 text-xs italic text-[hsl(var(--clinic-muted))]">Con cuenta puedes ver historial médico, próximas citas y cancelarlas desde tu panel.</p>

        <form onSubmit={submit} className="mt-8 rounded-2xl border border-[hsl(var(--clinic-border))] bg-white p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">Correo</span>
              <input required type="email" data-testid="mi-cita-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full rounded-xl border border-[hsl(var(--clinic-border))] bg-white px-4 py-3 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">Teléfono</span>
              <input required data-testid="mi-cita-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full rounded-xl border border-[hsl(var(--clinic-border))] bg-white px-4 py-3 text-sm" />
            </label>
          </div>
          <button
            type="submit" disabled={loading}
            data-testid="mi-cita-submit"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[hsl(var(--clinic-text))] px-6 py-3 text-sm font-semibold text-white disabled:opacity-60"
          >
            <Search size={16} /> {loading ? "Buscando…" : "Consultar"}
          </button>
        </form>

        {results !== null && (
          <div className="mt-10" data-testid="mi-cita-results">
            {results.length === 0 && (
              <div className="rounded-2xl border border-dashed border-[hsl(var(--clinic-border))] bg-white p-8 text-center text-sm text-[hsl(var(--clinic-muted))]">
                No encontramos citas con esos datos. Verifica el correo o <Link to="/reservar" className="text-[hsl(var(--clinic-secondary))]">reserva ahora</Link>.
              </div>
            )}
            {upcoming.length > 0 && (
              <>
                <h2 className="font-display text-2xl">Próximas</h2>
                <div className="mt-4 space-y-3">
                  {upcoming.map((b) => <BookingCard key={b.id} b={b} onCancel={() => cancel(b.id)} upcoming />)}
                </div>
              </>
            )}
            {past.length > 0 && (
              <>
                <h2 className="mt-10 font-display text-2xl">Anteriores</h2>
                <div className="mt-4 space-y-3">
                  {past.map((b) => <BookingCard key={b.id} b={b} />)}
                </div>
              </>
            )}
          </div>
        )}
      </section>
    </>
  );
}

function BookingCard({ b, onCancel, upcoming }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[hsl(var(--clinic-border))] bg-white p-5" data-testid={`booking-item-${b.id}`}>
      <div>
        <div className="font-display text-xl">{b.treatment_name}</div>
        <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-[hsl(var(--clinic-muted))]">
          <span className="inline-flex items-center gap-1"><CalendarDays size={14} /> {b.slot_date}</span>
          <span className="inline-flex items-center gap-1"><Clock size={14} /> {b.slot_time}</span>
          <span className="inline-flex items-center gap-1"><User size={14} /> {b.patient_name}</span>
        </div>
      </div>
      {upcoming && (
        <button onClick={onCancel} data-testid={`cancel-booking-${b.id}`} className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--clinic-border))] px-4 py-2 text-xs font-semibold text-[hsl(var(--clinic-secondary))]">
          <Trash2 size={14} /> Cancelar
        </button>
      )}
    </div>
  );
}
